import {
  INVOICE_SURCHARGE_MULTIPLIER,
  INVOICE_SURCHARGE_PERCENTAGE,
  JIMMY_LOVES_FOOD_ID,
  NETHERLANDS_COUNTRY_CODE,
} from "../constants/PricingConstants";
import { PAYMENT_METHOD_DIRECT_DEBIT, PAYMENT_METHOD_INVOICE } from "../constants/PaymentConstants";
import { JLF_PICKED_UP, MORNING_DELIVERY_CHARGE, MORNING_EXTRA_CHARGE_LABELS } from "../constants/OrderConstant";
import { calculateShippingCost } from "./orders";
import { GROWY } from "../constants/CompanyConstants";

// Let op: applyCustomerPricingToProduct en calculateOrderPricing zijn (deels) geport naar
// api/src/functions/pricing.ts, voor orders die de API zelf aanmaakt (EDI ORDERS-webhook).
// Wijzig je hier de prijslogica, pas die dan ook aan (en api/src/functions/pricing.spec.ts).

/**
 * Calculate final product price with payment method adjustment and customer discount
 * @param {number|string} basePrice - Original product price
 * @param {string} paymentMethod - "Invoice" or "Direct Debit"
 * @param {number} customerDiscountPercentage - Customer discount percentage (0-100)
 * @param {string} customerId - Customer ID for exceptions
 * @param {string} country - Customer country code
 * @param {string} productSize - Product size (small/medium)
 * @param {string} companyCode - Company code (GROWY or CHEFS_FARM) for pricing exceptions
 * @param {string} productGroup - Product group (Lettuce, Herbs, Microgreens, etc.)
 * @param {boolean} isCustomPriceListPrice - Whether this is a custom pricelist price (from customer or country list)
 * @param {boolean} hasPriceListCustomer - Whether the customer is linked to a customer-specific price list (disables invoice surcharge)
 * @param {object|null} countrySettings - Country-specific pricing settings from Firebase (pricing sub-object)
 * @returns {string} Final price as string with 2 decimal places
 */
export const calculateProductPrice = (
  basePrice,
  paymentMethod = PAYMENT_METHOD_DIRECT_DEBIT,
  customerDiscountPercentage = 0,
  customerId = null,
  country = null,
  productSize = null,
  companyCode = null,
  productGroup = null,
  isCustomPriceListPrice = false,
  hasPriceListCustomer = false,
  countrySettings = null,
) => {
  // Step 1: Convert base price to float
  let adjustedPrice = parseFloat(basePrice.toString().replace(",", "."));

  // Step 2: Apply payment method adjustment for Invoice
  // Exception: Customer 495 doesn't get Invoice surcharge
  // Exception: Growy customers don't get Invoice surcharge
  if (
    paymentMethod === PAYMENT_METHOD_INVOICE &&
    customerId !== JIMMY_LOVES_FOOD_ID &&
    companyCode !== GROWY &&
    !hasPriceListCustomer
  ) {
    const surchargeMultiplier =
      countrySettings?.invoiceSurchargePercentage != null
        ? 1 + countrySettings.invoiceSurchargePercentage / 100
        : INVOICE_SURCHARGE_MULTIPLIER;
    adjustedPrice = adjustedPrice * surchargeMultiplier;
  }

  // Step 3: Apply customer discount
  if (customerDiscountPercentage > 0) {
    adjustedPrice = adjustedPrice * ((100 - customerDiscountPercentage) / 100);
  }

  return adjustedPrice.toFixed(2);
};

/**
 * Get customer pricing info from customer object
 * @param {object} customer - Customer object with paymentMethod and customerSnelstartDiscount
 * @returns {object} Object with paymentMethod, discountPercentage, country, customerId, and companyCode
 */
export const getCustomerPricingInfo = (customer) => {
  return {
    paymentMethod: customer?.paymentMethod || PAYMENT_METHOD_DIRECT_DEBIT,
    discountPercentage: customer?.customerSnelstartDiscount || 0,
    country: customer?.country || NETHERLANDS_COUNTRY_CODE,
    customerId: customer?.customerId || customer?.id,
    companyCode: customer?.company || null,
  };
};

/**
 * Get the custom price for a product from the country price list for a given country code.
 * @param {object} product - Product object (needs id or productId)
 * @param {string} countryCode - Country code to look up (e.g. "NL")
 * @param {object} priceLists - Price lists data (from usePriceLists hook)
 * @returns {number|string|null} Custom price if a country price list with an override exists, otherwise null
 */
export const getCountryPriceForProduct = (product, countryCode, priceLists) => {
  if (!priceLists || !countryCode || !product) return null;

  const countryPriceList = priceLists.find((pl) => pl.type === "country" && pl.countryCode === countryCode);
  const countryPrice = countryPriceList?.customPrices?.[product.id || product.productId];

  return countryPrice ?? null;
};

/**
 * Apply pricing to a product for a specific customer
 * @param {object} product - Product object with price (deprecated fallback), size, and productGroup
 * @param {object} customer - Customer object
 * @param {object} priceLists - Optional price lists data (from usePriceLists hook)
 * @param {object|null} countriesMap - Optional map of country data keyed by country code (from useCountries hook)
 * @returns {string} Final price for the customer
 */
export const applyCustomerPricingToProduct = (product, customer, priceLists = null, countriesMap = null) => {
  const { paymentMethod, discountPercentage, country, customerId, companyCode } = getCustomerPricingInfo(customer);
  const hasPriceListCustomer = Boolean(customer?.priceListId);
  const effectiveDiscountPercentage = hasPriceListCustomer ? 0 : discountPercentage;
  const countrySettings = countriesMap?.[country]?.pricing || null;

  // deprecated: product.price is the last-resort fallback, to be removed once every
  // product resolves through priceLists (customer -> customer's country -> NL)
  let basePrice = product.price;
  let hasCustomPriceListPrice = false;

  if (priceLists) {
    // Step 1: Check customer-specific price list (highest priority)
    if (customer?.priceListId) {
      const customerPriceList = priceLists.find(
        (pl) => pl.id === customer.priceListId && (pl.type === "customer" || !pl.type),
      );
      const customPrice = customerPriceList?.customPrices?.[product.id || product.productId];
      if (customPrice) {
        basePrice = customPrice;
        hasCustomPriceListPrice = true;
      }
    }

    // Step 2: Check the customer's own country price list (fallback, no effect on surcharge/discount)
    if (!hasCustomPriceListPrice && country) {
      const countryPrice = getCountryPriceForProduct(product, country, priceLists);
      if (countryPrice) {
        basePrice = countryPrice;
        hasCustomPriceListPrice = true;
      }
    }

    // Step 3: Fall back to the NL country price list as a generic default
    if (!hasCustomPriceListPrice && country !== NETHERLANDS_COUNTRY_CODE) {
      const nlPrice = getCountryPriceForProduct(product, NETHERLANDS_COUNTRY_CODE, priceLists);
      if (nlPrice) {
        basePrice = nlPrice;
        hasCustomPriceListPrice = true;
      }
    }
  }

  return calculateProductPrice(
    basePrice,
    paymentMethod,
    effectiveDiscountPercentage,
    customerId,
    country,
    product.size,
    companyCode,
    product.productGroup,
    hasCustomPriceListPrice,
    hasPriceListCustomer,
    countrySettings,
  );
};

/**
 * Calculate invoice additional costs (2% surcharge on total)
 * @param {number} subtotalWithoutSurcharge - Subtotal without invoice surcharge
 * @param {string} paymentMethod - Payment method
 * @param {string} customerId - Customer ID for exceptions
 * @param {string} companyCode - Company code for additional exceptions
 * @param {boolean} hasPriceListCustomer - Whether the customer is linked to a price list (disables invoice surcharge)
 * @param {object|null} countrySettings - Country-specific pricing settings from Firebase (pricing sub-object)
 * @returns {number} Additional invoice costs
 */
export const calculateInvoiceAdditionalCosts = (
  subtotalWithoutSurcharge,
  paymentMethod,
  customerId = null,
  companyCode = null,
  hasPriceListCustomer = false,
  countrySettings = null,
) => {
  if (
    paymentMethod === PAYMENT_METHOD_INVOICE &&
    customerId !== JIMMY_LOVES_FOOD_ID &&
    companyCode !== GROWY &&
    !hasPriceListCustomer
  ) {
    const surchargeRate =
      countrySettings?.invoiceSurchargePercentage != null
        ? countrySettings.invoiceSurchargePercentage / 100
        : INVOICE_SURCHARGE_PERCENTAGE;
    return subtotalWithoutSurcharge * surchargeRate;
  }
  return 0;
};

/**
 * Calculate order subtotal from products array
 * @param {Array} products - Array of product objects with productPrice and itemCount
 * @returns {number} Subtotal amount
 */
export const calculateOrderSubtotal = (products) => {
  if (!products || products.length === 0) return 0;

  return products.reduce((acc, item) => {
    return acc + parseFloat(item.productPrice) * item.itemCount;
  }, 0);
};

/**
 * Calculate order subtotal without customer discount (for 2% surcharge calculation)
 * @param {Array} orderProducts - Array of order product objects
 * @param {object} customer - Customer object
 * @param {Array} allProducts - Array of all available products for price lookup
 * @param {object} priceLists - Optional price lists data (from usePriceLists hook)
 * @param {object|null} countriesMap - Optional map of country data keyed by country code (from useCountries hook)
 * @returns {number} Subtotal without customer discount
 */
export const calculateOrderSubtotalWithoutDiscount = (
  orderProducts,
  customer,
  allProducts,
  priceLists = null,
  countriesMap = null,
) => {
  if (!orderProducts || orderProducts.length === 0) return 0;

  return orderProducts.reduce((acc, item) => {
    const product = allProducts.find((p) => p.productId === item.productId);
    if (product) {
      // Apply surcharge if applicable, but no customer discount
      const priceWith2Percent = applyCustomerPricingToProduct(
        product,
        {
          paymentMethod: customer?.paymentMethod,
          customerSnelstartDiscount: 0, // No customer discount for this calculation
          customerId: customer?.customerId,
          country: customer?.country,
          company: customer?.company,
          priceListId: customer?.priceListId,
        },
        priceLists,
        countriesMap,
      );
      return acc + parseFloat(priceWith2Percent) * item.itemCount;
    }
    return acc;
  }, 0);
};

/**
 * Calculate shipping cost for an order
 * @param {number} subtotal - Order subtotal
 * @param {string} customerId - Customer ID
 * @param {Array} customers - Array of customer objects
 * @param {string} deliveryLabel - Delivery label
 * @returns {number} Shipping cost
 */
export const calculateOrderShippingCost = (subtotal, customerId, customers, deliveryLabel, deliveryDefaults = null) => {
  // No shipping for pickup orders or additional deliveries
  if (deliveryLabel === JLF_PICKED_UP) return 0;
  if (deliveryLabel === "ADD") return 0;

  return calculateShippingCost(subtotal, customerId, customers, deliveryDefaults);
};

/**
 * Calculate discount amount from discount code
 * @param {number} subtotal - Order subtotal
 * @param {object} discountCode - Discount code object with applied, amount, percentage
 * @returns {number} Discount amount
 */
export const calculateOrderDiscountAmount = (subtotal, discountCode) => {
  if (!discountCode?.applied) return 0;

  if (discountCode.amount > 0) {
    return discountCode.amount;
  } else if (discountCode.percentage > 0) {
    return subtotal * (discountCode.percentage / 100);
  }

  return 0;
};

/**
 * Calculate order total including all charges and discounts
 * @param {number} subtotal - Order subtotal
 * @param {number} shippingCost - Shipping cost
 * @param {string} deliveryLabel - Delivery label for morning charges
 * @param {number} discountAmount - Discount amount to subtract
 * @returns {number} Total order amount
 */
export const calculateOrderTotal = (subtotal, shippingCost, deliveryLabel, discountAmount = 0) => {
  let total = subtotal + shippingCost;

  // Add morning delivery charge if applicable
  if (MORNING_EXTRA_CHARGE_LABELS.includes(deliveryLabel)) {
    total += MORNING_DELIVERY_CHARGE;
  }

  // Subtract discount
  total -= discountAmount;

  return Math.max(0, total); // Ensure total is never negative
};

/**
 * Calculate bundle item prices with customer pricing applied
 * NOTE: Packaging items (isPackaging: true) are excluded from pricing adjustments
 * @param {Array} bundleItems - Array of bundle items with productId, quantity, price, isPackaging
 * @param {object} customer - Customer object with payment method and discount info
 * @param {Array} allProducts - Array of all available products for looking up product details
 * @param {object} priceLists - Optional price lists data (from usePriceLists hook)
 * @returns {Array} Bundle items with updated price field
 */
export const calculateBundleItemPrices = (
  bundleItems,
  customer,
  allProducts,
  priceLists = null,
  countriesMap = null,
) => {
  if (!bundleItems || bundleItems.length === 0) return [];

  return bundleItems.map((bundleItem) => {
    // Packaging items keep their original price (no invoice surcharge or customer discount)
    if (bundleItem.isPackaging) {
      return bundleItem;
    }

    // Find the product details for this bundle item
    const product = allProducts.find((p) => p.productId === bundleItem.productId || p.id === bundleItem.productId);

    if (!product) {
      // If product not found, return original bundle item
      return bundleItem;
    }

    // Create a temporary product object with the bundle item's base price
    const productForPricing = {
      ...product,
      price: bundleItem.price,
    };

    // Calculate the final price with invoice surcharge and customer discount
    const finalPrice = applyCustomerPricingToProduct(productForPricing, customer, priceLists, countriesMap);

    // Return bundle item with updated price
    return {
      ...bundleItem,
      price: parseFloat(finalPrice),
    };
  });
};

/**
 * Get the packaging material price from a bundle product
 * @param {object} product - Bundle product with packagingMaterial field
 * @returns {number} Packaging price (0 if no packaging)
 */
export const getBundlePackagingPrice = (product) => {
  if (!product?.packagingMaterial) return 0;
  return parseFloat(product.packagingMaterial.price || 0);
};

/**
 * Recalculate bundle item prices when a manual price override is applied.
 * The packaging material price is kept fixed; the remainder is distributed
 * proportionally across the non-packaging bundle items.
 * @param {object} product - Bundle product with bundleItems and packagingMaterial
 * @param {number|string} overridePrice - The manually entered total bundle price
 * @returns {Array} Bundle items with recalculated price field
 */
export const recalculateBundleItemPricesForOverride = (product, overridePrice) => {
  if (!product?.bundleItems || product.bundleItems.length === 0) return product?.bundleItems || [];

  const overridePriceNum = parseFloat(overridePrice);
  const packagingPrice = getBundlePackagingPrice(product);

  // Calculate total current price of all NON-PACKAGING bundle items
  const totalItemsPrice = product.bundleItems.reduce((sum, item) => {
    if (item.isPackaging) return sum; // Skip packaging items
    return sum + parseFloat(item.price || 0) * (item.quantity || 1);
  }, 0);

  if (totalItemsPrice === 0) return product.bundleItems;

  // Target price for items after subtracting fixed packaging price
  const targetItemsPrice = overridePriceNum - packagingPrice;

  return product.bundleItems.map((item) => {
    // Packaging items keep their original price
    if (item.isPackaging) {
      return item;
    }

    const itemPrice = parseFloat(item.price || 0);
    const quantity = item.quantity || 1;
    const ratio = (itemPrice * quantity) / totalItemsPrice;
    const newPricePerItem = (targetItemsPrice * ratio) / quantity;

    return {
      ...item,
      price: newPricePerItem,
    };
  });
};

/**
 * Determine bundle item pricing for reporting purposes.
 * The bundle price (finalBundlePrice) is ALWAYS the source of truth: bundle items are
 * redistributed proportionally so that their sum always equals the bundle price
 * (keeping packaging fixed). Bundle item prices are ONLY used for accurate reporting -
 * the bundle price itself is never recalculated from the individual item prices.
 * NOTE: Packaging items are marked with isPackaging flag and excluded from pricing adjustments
 * @param {object} product - Bundle product with bundleItems and packagingMaterial
 * @param {object} customer - Customer object (kept for signature compatibility, not used for item pricing)
 * @param {number|string} finalBundlePrice - Already-calculated total bundle price for this customer
 * @param {Array} allProducts - Array of all available products (kept for signature compatibility)
 * @param {object} priceLists - Optional price lists data (kept for signature compatibility)
 * @param {object|null} countriesMap - Optional map of country data (kept for signature compatibility)
 * @returns {Array} Bundle items with updated price field (including packaging if present)
 */
export const getBundleItemsWithCustomerPricing = (
  product,
  customer,
  finalBundlePrice,
  allProducts,
  priceLists = null,
  countriesMap = null,
) => {
  if (!product?.bundleItems || product.bundleItems.length === 0) return [];

  // Combine bundleItems with packagingMaterial (if present) and mark packaging items
  const allBundleItems = [...product.bundleItems];

  // Add packaging material to the items array with isPackaging flag
  if (product.packagingMaterial?.productId) {
    allBundleItems.push({
      ...product.packagingMaterial,
      isPackaging: true,
    });
  }

  // Mark existing items as non-packaging (unless already marked)
  const bundleItemsWithPackagingFlag = allBundleItems.map((item) => ({
    ...item,
    isPackaging: item.isPackaging || false,
  }));

  // Always redistribute the bundle price proportionally across items so the item prices
  // always sum to the bundle price. This applies whether the bundle price comes from the
  // product's own price, a bundle-level price list override, or any customer pricing.
  return recalculateBundleItemPricesForOverride(
    { ...product, bundleItems: bundleItemsWithPackagingFlag },
    finalBundlePrice,
  );
};

/**
 * Calculate all order pricing components at once
 * @param {Array} orderProducts - Array of order product objects
 * @param {object} customer - Customer object
 * @param {string} deliveryLabel - Delivery label (still needed for shipping and morning charges)
 * @param {Array} allProducts - Array of all available products
 * @param {boolean} addShippingCost - Whether to add shipping cost
 * @param {object} discountCode - Discount code object
 * @param {object} priceLists - Optional price lists data (from usePriceLists hook)
 * @param {object|null} countriesMap - Optional map of country data keyed by country code (from useCountries hook)
 * @returns {object} Object with all pricing components
 */
export const calculateOrderPricing = (
  orderProducts,
  customer,
  deliveryLabel,
  allProducts,
  addShippingCost = true,
  discountCode = null,
  priceLists = null,
  countriesMap = null,
  deliveryDefaults = null,
) => {
  const subtotal = calculateOrderSubtotal(orderProducts);
  const subtotalWithoutDiscount = calculateOrderSubtotalWithoutDiscount(
    orderProducts,
    customer,
    allProducts,
    priceLists,
    countriesMap,
  );

  const shippingCost = addShippingCost
    ? calculateOrderShippingCost(subtotal, customer?.id, [customer], deliveryLabel, deliveryDefaults)
    : 0;

  const discountAmount = calculateOrderDiscountAmount(subtotal, discountCode);
  const total = calculateOrderTotal(subtotal, shippingCost, deliveryLabel, discountAmount);

  const countrySettings = countriesMap?.[customer?.country || NETHERLANDS_COUNTRY_CODE]?.pricing || null;
  const invoiceAdditionalCosts = calculateInvoiceAdditionalCosts(
    subtotalWithoutDiscount,
    customer?.paymentMethod,
    customer?.customerId,
    customer?.company,
    Boolean(customer?.priceListId),
    countrySettings,
  );

  const defaultTaxRate = countrySettings?.defaultTaxRate ?? 9;
  const tax = subtotal * (defaultTaxRate / 100);

  return {
    subtotal,
    subtotalWithoutDiscount,
    shippingCost,
    discountAmount,
    total,
    invoiceAdditionalCosts,
    tax,
    morningDeliveryCharge: MORNING_EXTRA_CHARGE_LABELS.includes(deliveryLabel) ? MORNING_DELIVERY_CHARGE : 0,
  };
};
